import { resolvePluginDataSource } from '~/services/pipes/plugin-data-source.ts';
import { getClientFeedUrl } from '~/services/request/index.ts';
import { requestJsonData } from '~/utils/request-json-data.ts';
import type { FeedEntry } from '~/services/layout/index.ts';
import { ZAPP_STORAGE_UPDATED_EVENT } from '~/hooks/actions/defaultActions/sessionStorageSet.ts';
import { defaultCtxGetter } from '~/hooks/actions/utils/prepare-action-with-context.ts';
import { dispatchAudioPlayerClose } from '~/utils/audio-player.ts';
import { QueueManager } from './queue-manager.ts';
import { defaultQueueStorageAdapter } from './storage-adapter.ts';
import type { FeedResolver, PlayerObserver } from './types.ts';

export const defaultFeedResolver: FeedResolver = async (
  feed: string | Record<string, unknown>,
  contextEntry?: FeedEntry
): Promise<FeedEntry[]> => {
  const url =
    typeof feed === 'string'
      ? feed
      : (feed as Record<string, unknown>)?.url ||
        (feed as Record<string, unknown>)?.source;
  if (!url || typeof url !== 'string') return [];

  if (url.startsWith('pipesv2://')) {
    const handler = resolvePluginDataSource(url);
    if (handler) {
      const response = await handler(url);
      if (Array.isArray(response?.data?.entry)) {
        return response.data.entry;
      }
      if (Array.isArray(response?.data)) {
        return response.data;
      }
    }
    return [];
  }

  const rawClientUrl =
    url.startsWith('/') && !url.startsWith('/api/')
      ? url
      : url.startsWith('/api/') || url.startsWith('api/')
      ? url
      : getClientFeedUrl(url);

  const clientUrl = rawClientUrl.startsWith('/')
    ? rawClientUrl
    : `/${rawClientUrl}`;

  const response = await requestJsonData({ url: clientUrl });
  if (Array.isArray(response?.entry)) {
    return response.entry;
  }
  if (Array.isArray(response)) {
    return response;
  }
  return [];
};

const PROFILE_STORAGE_KEYS = ['user_account:profile', 'user_account.profile'];

/**
 * Resolves the currently active profile ID from session or local storage.
 *
 * @returns Active profile ID string or `null` if none set.
 */
export function getActiveProfileIdFromStorage(): string | null {
  for (const key of PROFILE_STORAGE_KEYS) {
    const raw = defaultCtxGetter(key);
    if (!raw) continue;

    try {
      const parsed: unknown = JSON.parse(raw);
      if (typeof parsed === 'string' && parsed.trim().length > 0) {
        return parsed.trim();
      }
      if (typeof parsed === 'number') {
        return String(parsed);
      }
      if (
        parsed &&
        typeof parsed === 'object' &&
        'id' in parsed &&
        (parsed as { id: unknown }).id != null
      ) {
        return String((parsed as { id: unknown }).id);
      }
    } catch {
      if (raw.trim().length > 0) {
        return raw.trim();
      }
    }
  }

  return null;
}

let activeProfileUnsubscribe: (() => void) | null = null;

export function setupProfileWatcher(manager: QueueManager): () => void {
  if (activeProfileUnsubscribe) {
    activeProfileUnsubscribe();
    activeProfileUnsubscribe = null;
  }

  if (typeof window === 'undefined') {
    return () => {};
  }

  const onProfileChange = (): void => {
    const newProfileId = getActiveProfileIdFromStorage();
    if (manager.getActiveProfileId() !== newProfileId) {
      void manager.switchProfile(newProfileId);
      dispatchAudioPlayerClose();
    }
  };

  window.addEventListener('storage', onProfileChange);
  window.addEventListener(ZAPP_STORAGE_UPDATED_EVENT, onProfileChange);

  const unsubscribe = (): void => {
    window.removeEventListener('storage', onProfileChange);
    window.removeEventListener(ZAPP_STORAGE_UPDATED_EVENT, onProfileChange);
    if (activeProfileUnsubscribe === unsubscribe) {
      activeProfileUnsubscribe = null;
    }
  };

  activeProfileUnsubscribe = unsubscribe;
  return unsubscribe;
}

export const defaultPlayerObserver: PlayerObserver = {
  subscribe(onTrackStarted: (entry: FeedEntry) => void | Promise<void>) {
    if (typeof window === 'undefined') {
      return () => {};
    }

    const handler = (event: Event) => {
      const entry = (event as CustomEvent)?.detail?.entry;
      if (entry) {
        void onTrackStarted(entry);
      }
    };

    window.addEventListener('audio_player.track_started', handler);
    return () => {
      window.removeEventListener('audio_player.track_started', handler);
    };
  },
};

export function setupClientQueue(): QueueManager {
  if (typeof window !== 'undefined') {
    (window as any).QueueManager = QueueManager;
  }

  const manager = QueueManager.isInitialized()
    ? QueueManager.instance
    : QueueManager.initialize({
        storage: defaultQueueStorageAdapter,
        feedResolver: defaultFeedResolver,
        playerObserver: defaultPlayerObserver,
      });

  setupProfileWatcher(manager);

  const activeProfileId = getActiveProfileIdFromStorage();
  if (manager.getActiveProfileId() !== activeProfileId) {
    void manager.switchProfile(activeProfileId);
  }

  return manager;
}
